import { create } from 'zustand';
import { AuthState, User, LoginCredentials } from '../types/auth';
import { authService } from '../services/authService';

/**
 * Authentication store using Zustand
 * Manages global auth state
 */
interface AuthStore extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithSSO: () => Promise<void>;
  completeSSOLogin: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.login(credentials);
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Login failed',
        isLoading: false,
      });
      throw error;
    }
  },

  loginWithSSO: async () => {
    set({ isLoading: true, error: null });
    // Navigates the browser away to Keycloak's login page - no state update needed here,
    // completeSSOLogin() picks up when the browser returns to /auth/callback.
    await authService.redirectToSSOLogin();
  },

  completeSSOLogin: async (code: string) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.handleSSOCallback(code);
      set({ user, isAuthenticated: true, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'SSO sign-in failed',
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
      set({ user: null, isAuthenticated: false, isLoading: false, error: null });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Logout failed',
        isLoading: false,
      });
    }
  },

  refreshToken: async () => {
    try {
      await authService.refreshToken();
    } catch (error) {
      set({ user: null, isAuthenticated: false, error: 'Session expired' });
      throw error;
    }
  },

  setUser: (user: User | null) => {
    set({ user, isAuthenticated: !!user });
  },

  clearError: () => {
    set({ error: null });
  },
}));
